import { useEffect, useMemo, useState } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { hasMsalRedirectInUrl } from "./creRedirectUtils";
import { isCreAccount, syncCreActiveAccount } from "./creAccountUtils";
import { setCreLoginIntent } from "./creLoginIntent";
import {
  MedMicAuthCoordinator,
} from "./medmicAuthCoordinator";
import { useMedMicAuthStore } from "./medmicAuthStore";
import CreExtensionLoginPage from "./CreExtensionLoginPage";
import { CRE_EXTENSION_PATH } from "../constants/routes";
import { consumeMedMicExplicitConnect } from "./medmicExplicitConnect";

/** Extension-only MSAL gate at /cre/extension */
export default function CreExtensionGate() {
  const { inProgress, instance, accounts } = useMsal();
  const navigate = useNavigate();
  const medmic = useMedMicAuthStore();
  const [initDone, setInitDone] = useState(false);

  const creAccounts = useMemo(
    () => accounts.filter(isCreAccount),
    [accounts],
  );

  useEffect(() => {
    setCreLoginIntent("extension");
    MedMicAuthCoordinator.startBridge();
    void MedMicAuthCoordinator.initialize();
  }, []);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    syncCreActiveAccount(instance);
  }, [instance, accounts, inProgress]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (hasMsalRedirectInUrl()) return;

    let cancelled = false;
    (async () => {
      await MedMicAuthCoordinator.initialize();
      const explicitConnect = consumeMedMicExplicitConnect();

      // Explicit disconnect latch: never silent-reconnect.
      if (MedMicAuthCoordinator.isDisconnected() && !explicitConnect) {
        if (!cancelled) setInitDone(true);
        MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
        return;
      }

      // Explicit Connect clears latch. Otherwise account-scoped bootstrap only.
      const ok = explicitConnect
        ? await MedMicAuthCoordinator.connect({
            source: "cre_extension_gate_explicit",
          })
        : await MedMicAuthCoordinator.bootstrap({
            source: "cre_extension_gate_bootstrap",
          });

      if (cancelled) return;
      setInitDone(true);
      if (ok) {
        navigate(`${CRE_EXTENSION_PATH}/handoff`, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creAccounts.length, inProgress, navigate]);

  if (inProgress !== InteractionStatus.None || hasMsalRedirectInUrl()) {
    return (
      <div className="cre-root cre-loading">
        <div className="cre-spinner" />
        <span>Authenticating…</span>
      </div>
    );
  }

  if (
    medmic.state === "authenticating" ||
    medmic.state === "validating" ||
    (!initDone && !medmic.disconnected)
  ) {
    return (
      <div className="cre-root cre-loading">
        <div className="cre-spinner" />
        <span>
          {medmic.state === "validating"
            ? "Validating MedMic session…"
            : "Preparing extension…"}
        </span>
      </div>
    );
  }

  if (medmic.state === "validation_failed") {
    return (
      <div className="cre-root cre-session-page">
        <div className="cre-login-bg" aria-hidden />
        <div className="cre-session-card">
          <h2 className="cre-session-error-title">Unable to connect MedMic</h2>
          <p className="cre-session-error-message">
            {medmic.error?.message ||
              "Your account could not be validated for MedMic. Please try again."}
          </p>
          <button
            type="button"
            className="cre-btn-signin"
            onClick={() => {
              setInitDone(false);
              void MedMicAuthCoordinator.connect({
                source: "gate_retry",
              }).then((ok) => {
                if (ok) {
                  navigate(`${CRE_EXTENSION_PATH}/handoff`, { replace: true });
                } else {
                  setInitDone(true);
                }
              });
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // disconnected / signed_out / no auto account → Connect UI
  if (
    medmic.disconnected ||
    medmic.state === "disconnected" ||
    medmic.state === "signed_out" ||
    creAccounts.length === 0
  ) {
    return <CreExtensionLoginPage />;
  }

  return (
    <div className="cre-root cre-loading">
      <div className="cre-spinner" />
      <span>Preparing extension…</span>
    </div>
  );
}
